import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import type { Category, SuggestedCategory, Transaction, TxKind } from '../api/types';
import { formatEuro, todayIso } from '../lib/format';
import { Sheet } from './Sheet';
import { amountFromText, applyKey, Keypad } from './Keypad';
import { Dot } from './ui';

interface Prefill {
  amount?: number;
  categoryId?: string;
  kind?: TxKind;
  note?: string | null;
  date?: string;
}

interface AddSheetValue {
  /** Apre il foglio vuoto, o precompilato (usato da "Ripeti"). */
  open: (prefill?: Prefill) => void;
  /** Apre il foglio sullo stesso movimento, in modifica. */
  edit: (transaction: Transaction) => void;
}

const AddSheetContext = createContext<AddSheetValue | null>(null);

export function useAddSheet(): AddSheetValue {
  const ctx = useContext(AddSheetContext);
  if (!ctx) throw new Error('useAddSheet fuori da AddSheetProvider');
  return ctx;
}

export function AddSheetProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Transaction | null>(null);
  const [prefill, setPrefill] = useState<Prefill | null>(null);

  const value = useMemo<AddSheetValue>(
    () => ({
      open: (p) => {
        setEditing(null);
        setPrefill(p ?? null);
        setIsOpen(true);
      },
      edit: (transaction) => {
        setEditing(transaction);
        setPrefill(null);
        setIsOpen(true);
      },
    }),
    [],
  );

  return (
    <AddSheetContext.Provider value={value}>
      {children}
      {isOpen && (
        <EntrySheet
          key={editing?.id ?? 'new'}
          editing={editing}
          prefill={prefill}
          onClose={() => setIsOpen(false)}
        />
      )}
    </AddSheetContext.Provider>
  );
}

function EntrySheet({
  editing,
  prefill,
  onClose,
}: {
  editing: Transaction | null;
  prefill: Prefill | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const initial = editing ?? prefill;

  const [kind, setKind] = useState<TxKind>(initial?.kind ?? 'EXPENSE');
  const [amountText, setAmountText] = useState(
    initial?.amount ? initial.amount.toFixed(2).replace('.', ',') : '',
  );
  const [categoryId, setCategoryId] = useState<string | null>(
    editing?.category.id ?? prefill?.categoryId ?? null,
  );
  const [date, setDate] = useState(editing?.date ?? prefill?.date ?? todayIso());
  const [note, setNote] = useState(editing?.note ?? prefill?.note ?? '');
  const [showDetails, setShowDetails] = useState(false);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const hour = new Date().getHours();
  const { data: suggested } = useQuery({
    queryKey: ['categories', 'suggested', kind, Math.floor(hour / 6)],
    queryFn: () => api.get<SuggestedCategory[]>(`/categories/suggested?kind=${kind}&hour=${hour}`),
  });
  const { data: allCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/categories'),
  });

  const selected = useMemo(() => {
    if (!categoryId) return null;
    const fromAll = allCategories?.find((c) => c.id === categoryId);
    if (fromAll) return { id: fromAll.id, name: fromAll.name, color: fromAll.color };
    const fromSuggested = suggested?.find((c) => c.id === categoryId);
    return fromSuggested ?? editing?.category ?? null;
  }, [categoryId, allCategories, suggested, editing]);

  const amount = amountFromText(amountText);
  const canSave = amount > 0 && !!categoryId;

  const save = useMutation({
    mutationFn: async () => {
      const body = { date, amount, kind, categoryId, note: note.trim() || null };
      return editing
        ? api.patch<Transaction>(`/transactions/${editing.id}`, body)
        : api.post<Transaction>('/transactions', body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      if (editing) {
        onClose();
        return;
      }
      // Inserimenti a raffica: il foglio resta aperto con l'importo azzerato.
      setSaved(true);
      setAmountText('');
      setNote('');
      window.setTimeout(() => setSaved(false), 1600);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Salvataggio non riuscito'),
  });

  useEffect(() => {
    if (amountText) setError(null);
  }, [amountText]);

  // La tastiera fisica resta utilizzabile su desktop.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (picking) return;
      if (/^[0-9]$/.test(e.key)) setAmountText((t) => applyKey(t, e.key));
      else if (e.key === ',' || e.key === '.') setAmountText((t) => applyKey(t, ','));
      else if (e.key === 'Backspace') setAmountText((t) => applyKey(t, 'del'));
      else if (e.key === 'Enter' && canSave) save.mutate();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [picking, canSave, save]);

  return (
    <Sheet open onClose={onClose} labelledBy="add-sheet-title">
      <h2 id="add-sheet-title" className="sr-only">
        {editing ? 'Modifica movimento' : 'Nuovo movimento'}
      </h2>

      {picking ? (
        <CategoryPicker
          categories={allCategories ?? []}
          onPick={(id) => {
            setCategoryId(id);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      ) : (
        <div className="flex flex-col gap-3 overflow-y-auto px-4 pt-1 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="grid grid-cols-2 gap-1 rounded-[var(--radius-control)] bg-surface-2 p-1">
            {(['EXPENSE', 'INCOME'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setKind(k);
                  setCategoryId(null);
                }}
                aria-pressed={kind === k}
                className={`rounded-lg py-2 text-sm font-medium transition ${
                  kind === k ? 'bg-surface text-text shadow-sm' : 'text-muted'
                }`}
              >
                {k === 'EXPENSE' ? 'Spesa' : 'Guadagno'}
              </button>
            ))}
          </div>

          <div className="pt-1 text-center">
            <div
              className="tnum text-[2.75rem] leading-none font-semibold tracking-tight"
              aria-live="polite"
              aria-label={`Importo ${amountText || '0'} euro`}
            >
              <span className={amountText ? '' : 'text-muted'}>
                {amountText ? `€ ${amountText}` : '€ 0'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-surface-2 px-3 py-1.5 text-sm"
            >
              {selected ? (
                <>
                  <Dot color={selected.color} />
                  {selected.name}
                </>
              ) : (
                <span className="text-muted">Scegli categoria</span>
              )}
              <span aria-hidden="true" className="text-muted">
                ▾
              </span>
            </button>
          </div>

          <Keypad onKey={(key) => setAmountText((t) => applyKey(t, key))} />

          <div className="flex flex-wrap gap-1.5">
            {(suggested ?? []).slice(0, 6).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategoryId(c.id)}
                aria-pressed={categoryId === c.id}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition ${
                  categoryId === c.id
                    ? 'border-accent bg-accent-soft'
                    : 'border-border bg-surface-2'
                }`}
              >
                <Dot color={c.color} />
                {c.name}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="rounded-full bg-surface-2 px-3 py-1.5 text-sm text-muted"
            >
              Tutte ▾
            </button>
          </div>

          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="self-start text-sm text-muted underline-offset-4 hover:underline"
            aria-expanded={showDetails}
          >
            {showDetails ? 'Nascondi dettagli' : 'Dettagli'}
          </button>

          {showDetails && (
            <div className="grid gap-2">
              <label className="text-sm">
                <span className="mb-1 block text-muted">Data</span>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full rounded-[var(--radius-control)] bg-surface-2 px-3 py-2.5"
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-muted">Nota</span>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={200}
                  placeholder="Facoltativa"
                  className="w-full rounded-[var(--radius-control)] bg-surface-2 px-3 py-2.5"
                />
              </label>
            </div>
          )}

          {error && (
            <p
              role="alert"
              className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-sm text-danger"
            >
              {error}
            </p>
          )}

          <button
            type="button"
            disabled={!canSave || save.isPending}
            onClick={() => save.mutate()}
            className="mt-1 w-full rounded-[var(--radius-control)] bg-accent py-3.5 text-base font-medium text-accent-text transition active:scale-[0.99] disabled:opacity-40"
          >
            {save.isPending
              ? 'Salvo...'
              : saved
                ? 'Salvato ✓'
                : editing
                  ? 'Aggiorna'
                  : `Salva ${amount > 0 ? formatEuro(amount) : ''}`}
          </button>
        </div>
      )}
    </Sheet>
  );
}

/** Elenco completo con ricerca, per quando i chip non bastano. */
function CategoryPicker({
  categories,
  onPick,
  onClose,
}: {
  categories: Category[];
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const normalized = query.trim().toLowerCase();

  const filtered = categories
    .filter((c) => !c.archived)
    .filter((c) => c.name.toLowerCase().includes(normalized))
    .sort((a, b) => b.transactionCount - a.transactionCount);

  const exactExists = categories.some((c) => c.name.toLowerCase() === normalized);

  const create = useMutation({
    mutationFn: (name: string) => api.post<Category>('/categories', { name }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['categories'] });
      onPick(created.id);
    },
  });

  const pick = useCallback((id: string) => onPick(id), [onPick]);

  return (
    <div className="flex max-h-[80dvh] flex-col px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="flex items-center gap-2 py-2">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca categoria"
          className="w-full rounded-[var(--radius-control)] bg-surface-2 px-3 py-2.5"
        />
        <button type="button" onClick={onClose} className="shrink-0 px-2 text-sm text-muted">
          Chiudi
        </button>
      </div>

      <div className="-mx-1 flex-1 overflow-y-auto">
        {filtered.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => pick(c.id)}
            className="flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-3 py-2.5 text-left transition active:bg-surface-2"
          >
            <Dot color={c.color} />
            <span className="flex-1">{c.name}</span>
            <span className="tnum text-xs text-muted">{c.transactionCount}</span>
          </button>
        ))}

        {normalized && !exactExists && (
          <button
            type="button"
            disabled={create.isPending}
            onClick={() => create.mutate(query.trim())}
            className="mt-1 w-full rounded-[var(--radius-control)] border border-dashed border-border px-3 py-2.5 text-left text-sm text-accent"
          >
            Crea categoria "{query.trim()}"
          </button>
        )}
      </div>
    </div>
  );
}
